import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useAuth } from '../auth/Auth';

import { User } from '../database/user';

import {
    UserApiContextType,
    getUser,
    getUserPublic,
    checkUserAccess,
    updateUser,
    listUsersPublic,
    deleteUser,
    deleteUserPublic,
    userGeneratePresignedUrl,
    uploadProfilePicToS3,
    getUsersBatch,
    updateUserSubscriptionOverride,
} from './userApi';

import { EventsApiContextType, getEvents } from './eventsApi';

import {
    VideosApiContextType,
    getYouTubePlaylistByType,
    getYouTubeVideoById,
} from './videosApi';

import {
    CommunityApiContextType,
    CommunityPostRequest,
    CommunityPostUpdateRequest,
    createCommunityPost,
    listCommunityPostsByOwner,
    createComment,
    deletePost,
    updateCommunityPost,
    communityPostGeneratePresignedUrl,
    uploadMediaToS3,
    setCommunityPostLike,
    listPostsByCategory,
    getPost,
} from './communityApi';

import { MusicApiContextType, getAlbums, getTopTracks, getTrackById } from './musicApi';
import { CommunityPost } from '../database/community';
import {
    PaymentApiContextType,
    createPaymentAccount,
    getPaymentAccount,
    paymentAccountLogin,
    setUpFuturePayment,
    getUserPaymentInfo,
    SubscriptionCheckoutRequest,
    subscriptionCheckout,
    subscriptionManage,
} from './paymentApi';
import {
    answerQuestion,
    AnswerQuestionRequest,
    createQuestion,
    CreateQuestionRequest,
    deleteQuestion,
    listQuestionsByOwner,
    listQuestionsByResponseType,
    listQuestionsByStatus,
    theSourceGeneratePresignedUrl,
    TheSourceApiContextType,
    uploadVideoToS3,
    MediaUploadRequest,
    listQuestionsByIsPrivate,
    setQuestionLike,
    SetLikeRequest,
    SetUpFuturePaymentsRequest,
} from './theSourceApi';
import { Question } from '../database/theSource';
import {
    createSupportRequest,
    HelpApiContextType,
    SupportRequestRequest,
} from './helpApi';
import {
    createForum,
    CreateForumRequest,
    createTopic,
    CreateTopicRequest,
    createThread,
    CreateThreadRequest,
    ForumApiContextType,
    listForums,
    listTopics,
    listThreads,
    putPresignedUrl,
    setThreadLike,
    deleteThread,
    deleteTopic,
    getTopic,
    getThread,
} from './forumApi';
import { ThreadSortOrder } from '../interstellar/TopicDetailPage';

/**
 * ApiContextType defines the interface of the API as available through ApiProvider.
 */
type ApiContextType = UserApiContextType &
    CommunityApiContextType &
    EventsApiContextType &
    MusicApiContextType &
    VideosApiContextType &
    PaymentApiContextType &
    TheSourceApiContextType &
    HelpApiContextType &
    ForumApiContextType;

const ApiContext = createContext<ApiContextType>(null!);

/**
 * @returns The current ApiContext value.
 */
export function useApi() {
    return useContext(ApiContext);
}

/**
 * ApiProvider provides access to API calls. It implements the ApiContextType interface.
 * ApiProvider must be a child of AuthProvider.
 * @param param0 React props. The only used prop is children.
 * @returns An ApiContext.Provider wrapping the provided children.
 */
export function ApiProvider({ children }: { children: ReactNode }) {
    const auth = useAuth();
    const idToken = auth.user?.cognitoUser?.session?.idToken.jwtToken ?? '';
    // const accessToken = auth.user?.cognitoUser?.session.accessToken.jwtToken ?? '';

    const value = useMemo(() => {
        return {
            checkUserAccess: () => checkUserAccess(idToken),
            getUser: () => getUser(idToken),
            getUserPublic: (username: string) => getUserPublic(username),
            updateUser: (update: Partial<User>) =>
                updateUser(idToken, update, auth.updateUser),
            getEvents: () => getEvents(),
            getAlbums: (includeAlbumGroup: string) => getAlbums(includeAlbumGroup),
            getTopTracks: () => getTopTracks(),
            getTrackById: (id: string) => getTrackById(id),
            getYouTubePlaylistByType: (mediaType: string, nextPageToken: string) =>
                getYouTubePlaylistByType(mediaType, nextPageToken),
            getYouTubeVideoById: (id: string) => getYouTubeVideoById(id),
            createCommunityPost: (req: CommunityPostRequest) =>
                createCommunityPost(idToken, req),
            deletePost: (post: CommunityPost) => deletePost(idToken, post),
            updateCommunityPost: (
                owner: string,
                createdAt: string,
                req: CommunityPostUpdateRequest,
            ) => updateCommunityPost(idToken, owner, createdAt, req),
            listPostsByCategory: (category: string, startKey?: string) =>
                listPostsByCategory(category, startKey),
            listCommunityPostsByOwner: (category: string, startKey?: string) =>
                listCommunityPostsByOwner(idToken, category, startKey),
            createComment: (postOwner: string, postCreatedAt: string, content: string) =>
                createComment(idToken, postOwner, postCreatedAt, content),
            listUsersPublic: (startKey?: string) => listUsersPublic(startKey),
            deleteUser: () => deleteUser(idToken),
            deleteUserPublic: (username: string) => deleteUserPublic(idToken, username),
            updateUserSubscriptionOverride: (
                username: string,
                subscriptionOverride: boolean,
            ) => updateUserSubscriptionOverride(idToken, username, subscriptionOverride),
            createPaymentAccount: () => createPaymentAccount(idToken),
            getPaymentAccount: () => getPaymentAccount(idToken),
            paymentAccountLogin: () => paymentAccountLogin(idToken),
            setUpFuturePayment: (req: SetUpFuturePaymentsRequest) =>
                setUpFuturePayment(idToken, req),
            createQuestion: (req: CreateQuestionRequest) => createQuestion(idToken, req),
            listQuestionsByIsPrivate: (isPrivate: string, startKey?: string) =>
                listQuestionsByIsPrivate(isPrivate, startKey),
            answerQuestion: (req: AnswerQuestionRequest) => answerQuestion(idToken, req),
            theSourceGeneratePresignedUrl: (question: Question, contentType: string) =>
                theSourceGeneratePresignedUrl(idToken, question, contentType),
            uploadVideoToS3: (req: MediaUploadRequest) => uploadVideoToS3(req),
            listQuestionsByOwner: (startKey?: string) =>
                listQuestionsByOwner(idToken, startKey),
            listQuestionsByResponseType: (responseType: string, startKey?: string) =>
                listQuestionsByResponseType(responseType, idToken, startKey),
            listQuestionsByStatus: (
                status: string,
                scanForwardIndex: boolean,
                startKey?: string,
            ) => listQuestionsByStatus(status, scanForwardIndex, idToken, startKey),
            deleteQuestion: (question: Question) => deleteQuestion(idToken, question),
            getUserPaymentInfo: () => getUserPaymentInfo(idToken),
            communityPostGeneratePresignedUrl: (
                post: CommunityPost,
                contentType: string,
            ) => communityPostGeneratePresignedUrl(idToken, post, contentType),
            uploadMediaToS3: (req: MediaUploadRequest) => uploadMediaToS3(req),
            userGeneratePresignedUrl: (contentType: string) =>
                userGeneratePresignedUrl(idToken, contentType),
            uploadProfilePicToS3: (req: MediaUploadRequest) => uploadProfilePicToS3(req),
            createSupportRequest: (req: SupportRequestRequest) =>
                createSupportRequest(idToken, req),
            setQuestionLike: (owner: string, createdAt: string, req: SetLikeRequest) =>
                setQuestionLike(idToken, owner, createdAt, req),
            getUsersBatch: (usernames: string[]) => getUsersBatch(usernames),
            getPost: (owner: string, createdAt: string) => getPost(owner, createdAt),
            setCommunityPostLike: (
                owner: string,
                createdAt: string,
                req: SetLikeRequest,
            ) => setCommunityPostLike(idToken, owner, createdAt, req),
            subscriptionCheckout: (request: SubscriptionCheckoutRequest) =>
                subscriptionCheckout(idToken, request),
            subscriptionManage: () => subscriptionManage(idToken),
            createForum: (req: CreateForumRequest) => createForum(idToken, req),
            listForums: (startKey?: string) => listForums(startKey),
            listTopics: (forumId: string, startKey?: string) =>
                listTopics(forumId, startKey),
            createTopic: (forumSk: string, req: CreateTopicRequest) =>
                createTopic(idToken, forumSk, req),
            listThreads: (
                topicSk: string,
                sortOrder: ThreadSortOrder,
                startKey?: string,
            ) => listThreads(topicSk, sortOrder, startKey),
            createThread: (forumSk: string, topicSk: string, req: CreateThreadRequest) =>
                createThread(idToken, forumSk, topicSk, req),
            putPresignedUrl: (
                forumSk: string,
                topicSk: string,
                threadSk: string,
                contentType: string,
            ) => putPresignedUrl(idToken, forumSk, topicSk, threadSk, contentType),
            setThreadLike: (owner: string, createdAt: string, req: SetLikeRequest) =>
                setThreadLike(idToken, owner, createdAt, req),
            deleteThread: (
                forumId: string,
                topicId: string,
                threadId: string,
                owner: string,
            ) => deleteThread(idToken, forumId, topicId, threadId, owner),
            deleteTopic: (forumId: string, topicId: string) =>
                deleteTopic(idToken, forumId, topicId),
            getTopic: (forumId: string, topicId: string) => getTopic(forumId, topicId),
            getThread: (topicId: string, threadId: string) =>
                getThread(topicId, threadId),
        };
    }, [idToken, auth.updateUser]);

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}
